/**
 * Hausje Product Detail V2-A — variant selection updates displayed price.
 * No cart / checkout mutations.
 */

function parseMoneyCents(cents) {
  return typeof cents === 'number' ? cents : null;
}

function findVariant(variants, selectedOptions) {
  return variants.find((v) => {
    const opts = [v.option1, v.option2, v.option3].filter((o) => o != null && o !== '');
    if (opts.length !== selectedOptions.length) {
      // Match by overlapping positions when Title/Default Title was suppressed
      return selectedOptions.every((val, i) => {
        const candidate = [v.option1, v.option2, v.option3][i];
        return candidate == null || candidate === val || String(candidate).toLowerCase() === 'default title';
      });
    }
    return selectedOptions.every((val, i) => opts[i] === val);
  });
}

function readSelectedOptions(root) {
  /** @type {string[]} */
  const values = [];
  const groups = root.querySelectorAll('[data-hausje-option-position]');
  /** @type {Map<number, string>} */
  const byPos = new Map();
  for (const input of groups) {
    if (!(input instanceof HTMLInputElement)) continue;
    if (!input.checked) continue;
    const pos = Number(input.dataset.hausjeOptionPosition || '0');
    byPos.set(pos, input.value);
  }
  const positions = [...byPos.keys()].sort((a, b) => a - b);
  for (const p of positions) values.push(byPos.get(p));
  return values;
}

function updatePrice(root, variant) {
  const priceRoot = root.querySelector('[data-hausje-price-live]');
  const current = root.querySelector('[data-hausje-price-current]');
  const compareWrap = root.querySelector('[data-hausje-price-compare]');
  const compareValue = root.querySelector('[data-hausje-price-compare-value]');
  if (!current || !variant) return;

  const price = parseMoneyCents(variant.price);
  const compare = parseMoneyCents(variant.compare_at_price);
  const showPrice = price != null && price > 0;

  if (priceRoot) {
    priceRoot.hidden = !showPrice;
  }

  current.textContent = showPrice ? variant.price_formatted || '' : '';

  const showCompare = showPrice && compare != null && compare > price;

  if (compareWrap && compareValue) {
    if (showCompare) {
      compareValue.textContent = variant.compare_at_price_formatted || '';
      compareWrap.hidden = false;
    } else {
      compareValue.textContent = '';
      compareWrap.hidden = true;
    }
  }
}

function initProductDetail(root) {
  const jsonEl = root.querySelector('[data-hausje-product-variants]');
  if (!jsonEl) return;

  let payload;
  try {
    payload = JSON.parse(jsonEl.textContent || '{}');
  } catch {
    return;
  }

  const variants = Array.isArray(payload.variants) ? payload.variants : [];
  if (!variants.length) return;

  const onChange = () => {
    const selected = readSelectedOptions(root);
    let variant = null;

    if (selected.length) {
      variant = variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        return selected.every((val) => opts.includes(val));
      });
    }

    if (!variant) variant = variants[0];
    updatePrice(root, variant);

    if (variant?.id) {
      root.dataset.selectedVariantId = String(variant.id);
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url);
      } catch {
        /* ignore */
      }
    }
  };

  root.addEventListener('change', (event) => {
    const t = event.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (!t.matches('[data-hausje-option-position]')) return;
    onChange();
  });

  onChange();
}

/**
 * Lightweight retailer outbound click analytics.
 * Never includes full affiliate URL. Navigation must work if this throws.
 */
function initRetailerClickEvents(root) {
  root.addEventListener(
    'click',
    (event) => {
      const t = event.target;
      if (!(t instanceof Element)) return;
      const anchor = t.closest('[data-hausje-retailer-cta]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const slot = anchor.closest('[data-hausje-offer-slot]');
      try {
        const detail = {
          canonical_key: slot?.getAttribute('data-canonical-key') || null,
          retailer: slot?.getAttribute('data-retailer') || null,
          placement: anchor.getAttribute('data-placement') || 'PDP',
          advertiser_id: slot?.getAttribute('data-advertiser-id') || null,
          network: slot?.getAttribute('data-network') || null,
        };
        document.dispatchEvent(
          new CustomEvent('hausje:retailer-click', { bubbles: true, detail })
        );
      } catch {
        /* navigation must proceed */
      }
    },
    { capture: false }
  );
}

function initGallery(root) {
  const gallery = root.querySelector('[data-hausje-gallery]');
  if (!(gallery instanceof HTMLElement)) return;
  const main = gallery.querySelector('.hausje-product-detail__image');
  if (!(main instanceof HTMLImageElement)) return;
  const thumbs = [...gallery.querySelectorAll('[data-hausje-gallery-thumb]')].filter(
    (el) => el instanceof HTMLButtonElement
  );
  if (!thumbs.length) return;

  const indexEl = gallery.querySelector('[data-hausje-gallery-index]');
  const stage = gallery.querySelector('[data-hausje-gallery-stage]');

  const activate = (thumb) => {
    if (!(thumb instanceof HTMLButtonElement)) return;
    const url = thumb.getAttribute('data-image-url');
    const srcset = thumb.getAttribute('data-image-srcset');
    if (!url) return;
    main.src = url;
    if (srcset) main.srcset = srcset;
    thumbs.forEach((btn) => {
      const active = btn === thumb;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    const idx = thumbs.indexOf(thumb);
    if (indexEl && idx >= 0) indexEl.textContent = String(idx + 1);
  };

  gallery.addEventListener('click', (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return;
    const thumb = t.closest('[data-hausje-gallery-thumb]');
    if (!(thumb instanceof HTMLButtonElement)) return;
    activate(thumb);
  });

  const step = (delta) => {
    const current = thumbs.findIndex((btn) => btn.classList.contains('is-active'));
    const next = (current < 0 ? 0 : current) + delta;
    const wrapped = ((next % thumbs.length) + thumbs.length) % thumbs.length;
    activate(thumbs[wrapped]);
  };

  // Mobile swipe on the main media frame (no carousel dependency).
  if (stage instanceof HTMLElement && thumbs.length > 1) {
    let startX = 0;
    let startY = 0;
    let tracking = false;
    stage.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.changedTouches?.[0];
        if (!touch) return;
        tracking = true;
        startX = touch.clientX;
        startY = touch.clientY;
      },
      { passive: true }
    );
    stage.addEventListener(
      'touchend',
      (event) => {
        if (!tracking) return;
        tracking = false;
        const touch = event.changedTouches?.[0];
        if (!touch) return;
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        step(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );
    stage.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        step(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        step(-1);
      }
    });
  }
}

function initDescriptionToggle(root) {
  const block = root.querySelector('[data-hausje-desc]');
  if (!(block instanceof HTMLElement)) return;
  const toggle = block.querySelector('[data-hausje-desc-toggle]');
  const intro = block.querySelector('[data-hausje-desc-intro]');
  const full = block.querySelector('[data-hausje-desc-full]');
  if (!(toggle instanceof HTMLButtonElement) || !(full instanceof HTMLElement)) return;

  toggle.addEventListener('click', () => {
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    const next = !expanded;
    toggle.setAttribute('aria-expanded', next ? 'true' : 'false');
    toggle.textContent = next ? 'Minder tonen' : 'Lees meer';
    full.hidden = !next;
    if (intro instanceof HTMLElement) intro.hidden = next;
  });
}

function boot() {
  document.querySelectorAll('[data-hausje-product-detail]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.dataset.hausjePdpReady === '1') return;
    el.dataset.hausjePdpReady = '1';
    initProductDetail(el);
    initRetailerClickEvents(el);
    initGallery(el);
    initDescriptionToggle(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

document.addEventListener('shopify:section:load', boot);
